import { z } from 'zod';
import { Scenario, type CreateScenarioBody, type UpdateScenarioBody } from '@exigo/shared';
import { api, ApiError } from '../api/client';

/**
 * Server-backed scenario storage — replaces the browser-sessionStorage stopgap
 * this module used to be. A scenario now survives a browser restart and a lost
 * laptop, and "Finalize" persists something durable.
 *
 * Every caller goes through the `scenarioStore` interface below, unchanged
 * from before — the swap from sessionStorage to `/api/v1/scenarios` touches
 * nothing else. `code_practise.md` §2 on why a repository interface earns
 * its keep, and see `migrateLegacySessionScenarios` at the bottom of this
 * file for carrying over whatever a tab still has sitting in the old
 * sessionStorage key from before this shipped.
 */

export type ScenarioStatus = Scenario['status'];
export type ScenarioSummary = NonNullable<Scenario['summary']>;

/** Kept as the exported name every caller already imports. */
export type ScenarioRecord = Scenario;

async function list(): Promise<ScenarioRecord[]> {
  return api.get('/scenarios', z.array(Scenario));
}

async function get(id: string): Promise<ScenarioRecord | null> {
  try {
    return await api.get(`/scenarios/${id}`, Scenario);
  } catch (err) {
    // A 404 (not found, or belongs to someone else — see security.md §4) is
    // "does not exist" here, matching the sessionStorage version's contract.
    if (isNotFound(err)) return null;
    throw err;
  }
}

/**
 * Field-by-field rather than a spread of the caller's object: `ScenarioListPage`'s
 * duplicate flow spreads an entire existing `ScenarioRecord` (id/createdAt/
 * updatedAt included) into the object it passes here, which the type system
 * allows through a spread even though the parameter type omits those fields.
 * A `.strict()` request body would reject those extra keys outright, so this
 * boundary picks exactly what the API accepts no matter what a caller hands in.
 */
async function create(
  record: Omit<ScenarioRecord, 'id' | 'ownerUserId' | 'createdAt' | 'updatedAt'>,
): Promise<ScenarioRecord> {
  const body: CreateScenarioBody = {
    name: record.name,
    customerName: record.customerName,
    opportunityRef: record.opportunityRef,
    guideVersion: record.guideVersion,
    input: record.input,
    promoInput: record.promoInput,
    promoCatalogue: record.promoCatalogue,
    status: record.status,
    ownerName: record.ownerName,
    summary: record.summary,
  };
  return api.post('/scenarios', Scenario, body);
}

async function update(
  id: string,
  patch: Partial<Omit<ScenarioRecord, 'id' | 'ownerUserId' | 'createdAt' | 'updatedAt'>>,
): Promise<ScenarioRecord | null> {
  try {
    return await api.patch(`/scenarios/${id}`, Scenario, patch satisfies UpdateScenarioBody);
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
}

async function remove(id: string): Promise<void> {
  try {
    await api.delete(`/scenarios/${id}`, z.void());
  } catch (err) {
    // Already gone is an acceptable outcome for a delete — the sessionStorage
    // version's `remove` was likewise silent about a missing id.
    if (!isNotFound(err)) throw err;
  }
}

function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export const scenarioStore = { list, get, create, update, remove };

/**
 * A 1–5 "setup progress" indicator for the card grid. Purely a UI affordance —
 * it gates nothing and is not a business rule, so it lives here rather than
 * in the engine.
 */
export function setupProgress(record: ScenarioRecord): { done: number; total: number } {
  const { input } = record;
  const steps = [
    true, // area/segment/market always set on creation
    Object.values(input.capability.specs).some((s) => s !== 'none') ||
      Object.values(input.capability.designations).some((s) => s !== 'none'),
    input.workloads.some((w) => w.amount > 0) ||
      input.manualAcr.incremental > 0 ||
      input.seats > 0 ||
      input.acv > 0,
    Object.values(input.prequalification).some((v) => v !== 0 && v !== false && v !== 'commercial'),
    record.summary !== null,
  ];
  return { done: steps.filter(Boolean).length, total: steps.length };
}

// --- One-time carry-over from the old sessionStorage key --------------------

/** The key the sessionStorage-only version of this module used. */
const LEGACY_STORAGE_KEY = 'exigo.mci.scenarios.session';
/** Set once this tab has attempted the carry-over, so it never repeats and
 *  never double-creates the same scenarios on a later reload in the same tab. */
const MIGRATED_MARKER_KEY = 'exigo.mci.scenarios.migrated';

interface LegacyScenarioRecord {
  name: string;
  customerName: string | null;
  opportunityRef: string | null;
  guideVersion: string;
  input: unknown;
  promoInput: unknown;
  promoCatalogue: unknown;
  status: string;
  ownerName: string | null;
  summary: unknown;
}

/**
 * Carries over whatever is sitting in a tab's old sessionStorage key into the
 * database, the first time this code runs in that tab. sessionStorage is
 * cleared on tab close by design, so this is really only about not losing
 * work already in progress in a tab open at the moment this feature ships —
 * not a general migration path.
 *
 * Never throws: a failed carry-over must not block sign-in or crash the app.
 * A record that fails to migrate simply stays in the old key and is retried
 * on the next call in a fresh tab (the marker is only set after this function
 * runs once, successfully or not, in *this* tab).
 */
export async function migrateLegacySessionScenarios(): Promise<void> {
  try {
    if (sessionStorage.getItem(MIGRATED_MARKER_KEY)) return;

    const raw = sessionStorage.getItem(LEGACY_STORAGE_KEY);
    sessionStorage.setItem(MIGRATED_MARKER_KEY, '1');
    if (!raw) return;

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;

    for (const legacy of parsed as LegacyScenarioRecord[]) {
      try {
        await create({
          name: legacy.name,
          customerName: legacy.customerName,
          opportunityRef: legacy.opportunityRef,
          guideVersion: legacy.guideVersion,
          input: legacy.input as ScenarioRecord['input'],
          promoInput: legacy.promoInput as ScenarioRecord['promoInput'],
          promoCatalogue: legacy.promoCatalogue as ScenarioRecord['promoCatalogue'],
          status: legacy.status as ScenarioStatus,
          ownerName: legacy.ownerName,
          summary: legacy.summary as ScenarioRecord['summary'],
        });
      } catch (err) {
        // One bad record must not stop the rest from migrating.
        console.error('Could not migrate a scenario from the previous session storage.', err);
      }
    }

    sessionStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (err) {
    console.error('Legacy scenario migration failed.', err);
  }
}
